import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, CheckCircle2, MessageSquare, Phone, 
  ArrowRight, Zap, RefreshCw, Check, Brain, History, 
  Sparkles, UserCheck, Timer, CalendarClock, Plane, Play 
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';

const HygieneRecallSlide = () => {
  const [animationStep, setAnimationStep] = useState(0);

  // Animation timeline loop
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % 6);
      // 0: Initial Call
      // 1: Call Back Later
      // 2: Intelligent Follow-up (Education)
      // 3: Out of Town
      // 4: Memory Trigger (Few Weeks Later)
      // 5: Booking
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const getTimelineEvents = () => {
    const events = [];

    // Step 0: Initial Call with clinical reasoning
    if (animationStep >= 0) {
      events.push({
        type: "voice",
        title: "Initial Outreach",
        time: "Oct 12, 10:00 AM",
        description: "Stephanie called James. Referenced his early periodontitis from chart notes — \"With your history, staying on top of cleanings is really important to prevent progression.\" Patient was receptive but busy.",
        meta: "Clinical Reasoning · Chart-Aware"
      });
    }

    // Step 1: Call Back Later Logic
    if (animationStep >= 1) {
      events.push({
        type: "system",
        title: "Smart Reschedule",
        time: "Oct 12, 10:01 AM",
        description: "Stephanie understood he was busy (not declining) and scheduled a retry for 4 PM — the time he mentioned works better.",
        meta: "Intent Understood · Auto-scheduled"
      });
    }

    // Step 2: Follow-up picks up where they left off
    if (animationStep >= 2) {
      events.push({
        type: "voice",
        title: "Follow-up Call (Continued Conversation)",
        time: "Oct 12, 4:00 PM",
        description: "Stephanie resumed the conversation naturally: \"Hi James, we spoke this morning — I wanted to follow up on getting that cleaning scheduled. With the early periodontitis we discussed, Dr. Chen really recommends coming in soon.\"",
        meta: "Patient Memory · Continued Context"
      });
    }

    // Step 3: Out of Town — memory stored
    if (animationStep >= 3) {
      events.push({
        type: "memory",
        title: "Travel Delay Detected",
        time: "Oct 12, 4:05 PM",
        description: "James mentioned heading to Europe for 3 weeks. Stephanie stored this context and set an automatic reminder — no manual follow-up needed from staff.",
        meta: "Status: Snoozed (Travel · Returns ~Nov 2)"
      });
    }

    // Step 4: Memory Trigger — picks up context from 3 weeks ago
    if (animationStep >= 4) {
      events.push({
        type: "system",
        title: "Memory Triggered — 3 Weeks Later",
        time: "Nov 2, 10:00 AM",
        description: "Stephanie loaded full conversation history: last call sentiment, travel reason, clinical urgency (periodontitis), and preferred call time (afternoon). Ready to re-engage.",
        meta: "Full Context Loaded"
      });
    }

    // Step 5: Booking with memory + clinical reasoning
    if (animationStep >= 5) {
      events.push({
        type: "success",
        title: "Appointment Booked",
        time: "Nov 2, 2:00 PM",
        description: "\"Welcome back from Europe, James! Hope the trip was great. I wanted to circle back on that cleaning — with your periodontitis history, Dr. Chen doesn't want us to wait much longer.\" Booked Nov 5 at 10 AM.",
        meta: "PMS Updated · Perio Flag Noted"
      });
    }

    return events;
  };

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/stephanie.png" alt="Stephanie" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Stephanie</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Hygiene Recall</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context - Expanded Width */}
            <div className="w-1/2 flex flex-col justify-center space-y-6">
                
                <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight">
                    Stephanie runs <span className="text-blue-600">intelligent AI voice campaigns</span> to book more hygiene.
                </h2>
                
                <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
                    Standard SMS reminders are too plain and often ignored. Stephanie runs intelligent, conversational campaigns via Voice & SMS to educate patients and book them directly into the correct operatory.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                    <div className="flex flex-col gap-3 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg mb-1">Patient Memory</h4>
                            <p className="text-sm text-slate-500 leading-relaxed">Remembers every prior interaction — what was said, the patient's sentiment, and why they delayed. Picks up the next conversation right where the last one left off.</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg mb-1">Clinical Reasoning</h4>
                            <p className="text-sm text-slate-500 leading-relaxed">Reads chart notes and uses clinical context — like a perio diagnosis — to explain why coming in matters. Not generic reminders, personalized clinical nudges.</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg mb-1">Intelligent Voice</h4>
                            <p className="text-sm text-slate-500 leading-relaxed">Holds real, natural conversations — not robocalls. Educates patients, handles objections, and books directly into the correct operatory.</p>
                        </div>
                    </div>
                </div>

                        {/* Demo Button Overlay */}
                        <div className="absolute bottom-6 right-6 z-50 flex items-center gap-4">
                            {/* Audio Wave Animation */}
                            <div className="flex items-center gap-1 h-8">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            height: [8, 24, 8],
                                            opacity: [0.5, 1, 0.5]
                                        }}
                                        transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            delay: i * 0.1,
                                            ease: "easeInOut"
                                        }}
                                        className="w-1 bg-blue-500 rounded-full"
                                    />
                                ))}
                            </div>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="group relative inline-flex items-center gap-3 bg-slate-900/90 backdrop-blur-md px-5 py-3 rounded-full shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300"
                                    >
                                        <div className="relative flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                                            <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                                            <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                                        </div>
                                        <div className="text-left pr-1">
                                            <div className="text-xs font-bold text-white">Watch Live Demo</div>
                                            <div className="text-[10px] text-slate-300">2 min video</div>
                                        </div>
                                    </motion.button>
                                </DialogTrigger>
                                <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
                                    <div className="relative w-full pt-[62.5%]">
                                        <iframe 
                                            src="https://www.loom.com/embed/09fe838559954860bfc461e6d88bd3a2" 
                                            frameBorder="0" 
                                            webkitallowfullscreen="true" 
                                            mozallowfullscreen="true" 
                                            allowFullScreen 
                                            className="absolute top-0 left-0 w-full h-full"
                                        ></iframe>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
            </div>

            {/* Right Side: Narrower Animation Card */}
            <div className="w-1/2 h-full flex items-center justify-center lg:justify-end">
                <motion.div 
                    initial={{ x: 40, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full max-w-md h-[700px] bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col relative"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 bg-white z-10">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900">James Wilson</h3>
                                <p className="text-sm text-slate-500">Overdue: 6 months · Early periodontitis</p>
                            </div>
                            <div className="flex gap-2">
                                <Badge variant="outline" className={`${animationStep === 5 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500'}`}>
                                    {animationStep === 5 ? 'Booked' : 'Active'}
                                </Badge>
                            </div>
                        </div>
                        
                        {/* Dynamic Status Banner */}
                        <AnimatePresence mode='wait'>
                            {animationStep === 3 && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="bg-yellow-50 border border-yellow-100 rounded-xl p-3 flex gap-3 items-center"
                                >
                                    <Plane className="w-5 h-5 text-yellow-600" />
                                    <div className="text-xs text-yellow-800">
                                        <span className="font-bold">Patient Travelling.</span> Snoozed until Nov 2.
                                    </div>
                                </motion.div>
                            )}
                            {animationStep === 5 && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="bg-green-50 border border-green-100 rounded-xl p-3 flex gap-3 items-center"
                                >
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    <div className="text-xs text-green-800">
                                        <span className="font-bold">Success!</span> Booked for Nov 5 at 10 AM.
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Timeline */}
                    <div className="flex-1 overflow-hidden relative bg-slate-50/50">
                        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200" />
                        
                        <div className="p-6 space-y-6 h-full overflow-y-auto">
                            <AnimatePresence initial={false}>
                                {getTimelineEvents().map((event, idx) => (
                                    <motion.div 
                                        key={event.title + event.time} // Unique key based on content
                                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        transition={{ duration: 0.4 }}
                                        className="relative pl-10"
                                    >
                                        <div className={`absolute left-[-11px] top-0 w-8 h-8 rounded-full border-4 border-slate-50 flex items-center justify-center z-10 shadow-sm ${
                                            event.type === 'system' ? 'bg-slate-200 text-slate-500' :
                                            event.type === 'voice' ? 'bg-indigo-100 text-indigo-600' :
                                            event.type === 'memory' ? 'bg-amber-100 text-amber-600' :
                                            'bg-green-100 text-green-600'
                                        }`}>
                                            {event.type === 'system' && <Zap className="w-3.5 h-3.5" />}
                                            {event.type === 'voice' && <Phone className="w-3.5 h-3.5" />}
                                            {event.type === 'memory' && <Brain className="w-3.5 h-3.5" />}
                                            {event.type === 'success' && <Check className="w-3.5 h-3.5" />}
                                        </div>
                                        
                                        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="font-bold text-slate-800 text-sm">{event.title}</span>
                                                <span className="text-[10px] text-slate-400 font-mono">{event.time}</span>
                                            </div>
                                            <p className="text-xs text-slate-600 leading-relaxed mb-2">
                                                {event.description}
                                            </p>
                                            <Badge variant="secondary" className="bg-slate-50 text-slate-500 text-[10px] font-medium border border-slate-100 px-1.5 py-0">
                                                {event.meta}
                                            </Badge>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Footer/Controls Mock */}
                    <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 px-6">
                        <span>AI Agent Active</span>
                        <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span>Monitoring</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

export default HygieneRecallSlide;

